import {
  PrimaryButton,
  SecondaryButton,
} from '@src/components/common/dialogStyles';
import Spinner from '@src/components/common/Spinner';
import { MODAL_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import { VideoVersionList } from '@src/components/stage/VideoVersionList';
import InfoIcon from '@src/svgs/InfoIconAlt';
import { formatDateTime } from '@src/utility/dateFormat';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { useClipPublishRecords } from './hooks/useClipPublishRecords';
import { useClipVersionHistory } from './hooks/useClipVersionHistory';

import type { ClipVideoCreatorAdapter } from './hooks/ClipVideoCreatorAdapter';

const PREVIEW_INFO_TOOLTIP_ID = 'clip-version-preview-info';

/**
 * The clip preview's History tab: the active variant's saved versions, the same
 * list as Creator Studio's History tab. Picking one previews it read-only in the
 * clip; only Restore saves it (see `ClipVersionPreviewStore`).
 */
export const ClipVersionHistoryPanel = observer(
  ({
    adapter,
    isActive,
  }: {
    adapter: ClipVideoCreatorAdapter;
    /** The tab is showing — nothing is fetched while it is not. */
    isActive: boolean;
  }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    // Shares the clip's Downloads tab cache, which the modal clears on close.
    const { records: publishRecords } = useClipPublishRecords(
      isActive ? adapter.currentVideo?.id : undefined,
      null,
    );
    const preview = adapter.versionPreview;
    const history = useClipVersionHistory({
      isOpen: isActive,
      videoId: adapter.currentVideo?.id,
      lastSavedAt: adapter.lastSavedAt,
      preview,
    });
    // The banner opens on the click, not when the version lands: the pick
    // still downloading wins over the one already showing.
    const bannerVersion = preview.pendingVersion ?? preview.previewingVersion;
    const isOpening = !!preview.pendingVersion;

    // The preview outlives the tab (its transcript is read on Transcript, the
    // tag above the player says it is showing), but not the modal.
    useEffect(() => () => preview.exit(), [preview]);

    return (
      <Container ref={containerRef} data-testid="clip-version-history-panel">
        {bannerVersion && (
          <PreviewBanner
            role="status"
            data-testid="clip-version-preview-banner"
          >
            <BannerTitle>
              Viewing {formatDateTime(bannerVersion.meta.created_at)}
              <InfoButton
                type="button"
                aria-label="About viewing a saved version"
                data-tooltip-id={PREVIEW_INFO_TOOLTIP_ID}
                data-tooltip-content="Editing is paused. Restoring saves this version as the latest; the current clip stays in History."
              >
                <InfoIcon width="16" height="16" />
              </InfoButton>
              {/* Last, so the text and icon never move while it comes and goes. */}
              {isOpening && <Spinner size="sm" />}
            </BannerTitle>
            <Tooltip
              id={PREVIEW_INFO_TOOLTIP_ID}
              place="bottom"
              noArrow
              opacity={1}
              style={MODAL_TOOLTIP_STYLE}
            />
            <BannerActions>
              <SecondaryButton type="button" onClick={preview.exit}>
                Back to Current
              </SecondaryButton>
              <PrimaryButton
                type="button"
                disabled={isOpening}
                onClick={preview.commit}
              >
                Restore This Version
              </PrimaryButton>
            </BannerActions>
          </PreviewBanner>
        )}
        {history.error && <ErrorText role="alert">{history.error}</ErrorText>}
        <VideoVersionList
          versions={history.versions}
          isLoading={history.isLoading}
          currentVersionId={history.currentVersionId}
          pendingVersionId={preview.pendingVersion?.versionId}
          onSelect={history.select}
          page={history.page}
          totalPages={history.totalPages}
          onPageChange={history.setPage}
          sidebarRef={containerRef}
          emptyMessage="No saved versions of this clip yet. Edits save automatically."
          publishRecords={publishRecords}
        />
      </Container>
    );
  },
);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const PreviewBanner = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  margin: 0 var(--spacing-sm) var(--spacing-sm);
  padding: var(--spacing-md);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  background: var(--surface-elevated);
`;

const BannerTitle = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semi-bold);
  color: var(--foreground);
`;

const InfoButton = styled.button`
  display: inline-flex;
  padding: 0;
  border: none;
  background: none;
  cursor: help;
  color: var(--text-muted);

  &:hover,
  &:focus-visible {
    color: var(--foreground);
  }
`;

const BannerActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-sm);
`;

const ErrorText = styled.p`
  margin: 0;
  padding-inline: var(--spacing-md);
  font-size: var(--text-xs);
  color: var(--destructive);
`;
